import React, { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';

const round2 = (n: number) => Math.round(n * 100) / 100;
const fmt = (n: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number.isFinite(n) ? n : 0);

interface PriceHeadlineProps {
  /** Prezzo pieno della riga (deal_services.amount). */
  amount: number;
  /** Prezzo scontato, se impostato (deal_services.discounted_amount). */
  discountedAmount?: number | null;
  onChange: (next: { amount: number; discounted_amount: number | null }) => void;
  /** Suffisso del periodo: " /mese", " /anno"… vuoto per le una tantum. */
  periodSuffix?: string;
  /** Testo esplicativo quando il prezzo della riga sta tutto negli add-on. */
  zeroHint?: string | null;
}

/**
 * Il prezzo come titolo della riga: scontato in evidenza, pieno barrato
 * accanto, percentuale di sconto in pillola. Al click diventa un editor con
 * tre campi che si inseguono — listino, sconto %, prezzo finale — così il
 * commerciale scrive quello che ha in testa senza fare il conto a mano.
 */
export function PriceHeadline({
  amount,
  discountedAmount,
  onChange,
  periodSuffix = '',
  zeroHint,
}: PriceHeadlineProps) {
  const [editing, setEditing] = useState(false);
  const netRef = useRef<HTMLInputElement>(null);

  const full = Number.isFinite(amount) ? amount : 0;
  const net = discountedAmount != null ? discountedAmount : full;
  const hasDiscount = discountedAmount != null && net < full - 0.005;
  const pct = full > 0 ? round2((1 - net / full) * 100) : 0;

  // Buffer di digitazione: senza, riscrivere "10,5" verrebbe normalizzato a
  // ogni tasto e il cursore salterebbe.
  const [draft, setDraft] = useState<{ full: string; pct: string; net: string } | null>(null);

  useEffect(() => {
    if (editing) {
      setDraft({ full: String(full), pct: String(pct), net: String(net) });
      setTimeout(() => netRef.current?.select(), 0);
    } else {
      setDraft(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const num = (s: string) => {
    const v = parseFloat(s.replace(',', '.'));
    return Number.isFinite(v) && v >= 0 ? v : 0;
  };

  const commitFull = (raw: string) => {
    const nextFull = num(raw);
    const nextNet = pct > 0 ? round2(nextFull * (1 - pct / 100)) : null;
    setDraft((d) => (d ? { ...d, full: raw, net: nextNet != null ? String(nextNet) : String(nextFull) } : d));
    onChange({ amount: nextFull, discounted_amount: nextNet });
  };

  const commitPct = (raw: string) => {
    const p = Math.min(100, num(raw));
    const nextNet = p > 0 ? round2(full * (1 - p / 100)) : null;
    setDraft((d) => (d ? { ...d, pct: raw, net: String(nextNet ?? full) } : d));
    onChange({ amount: full, discounted_amount: nextNet });
  };

  const commitNet = (raw: string) => {
    const n = num(raw);
    const nextNet = n >= full ? null : n;
    setDraft((d) =>
      d ? { ...d, net: raw, pct: full > 0 && nextNet != null ? String(round2((1 - nextNet / full) * 100)) : '0' } : d,
    );
    onChange({ amount: full, discounted_amount: nextNet });
  };

  if (editing && draft) {
    const fieldCls =
      'px-2 py-1 text-[14px] tabular-nums rounded bg-black/[0.03] dark:bg-white/[0.06] border border-transparent text-black dark:text-white focus:bg-white dark:focus:bg-gray-800/60 focus:border-[#2383E2] focus:outline-none focus:ring-2 focus:ring-[#2383E2]/20 transition-colors';
    return (
      <div className="py-1">
        <div className="flex items-end gap-2.5 flex-wrap">
          <label className="flex flex-col gap-0.5">
            <span className="text-[11px] font-medium text-black/55 dark:text-white/55">Prezzo di listino</span>
            <input
              className={`${fieldCls} w-[116px]`}
              inputMode="decimal"
              value={draft.full}
              onFocus={(e) => e.target.select()}
              onChange={(e) => commitFull(e.target.value)}
            />
          </label>
          <span className="text-[12px] text-black/40 dark:text-white/40 pb-1.5">meno</span>
          <label className="flex flex-col gap-0.5">
            <span className="text-[11px] font-medium text-black/55 dark:text-white/55">Sconto %</span>
            <input
              className={`${fieldCls} w-[78px]`}
              inputMode="decimal"
              value={draft.pct}
              onFocus={(e) => e.target.select()}
              onChange={(e) => commitPct(e.target.value)}
            />
          </label>
          <span className="text-[12px] text-black/40 dark:text-white/40 pb-1.5">=</span>
          <label className="flex flex-col gap-0.5">
            <span className="text-[11px] font-medium text-black/55 dark:text-white/55">Prezzo finale</span>
            <input
              ref={netRef}
              className={`${fieldCls} w-[116px]`}
              inputMode="decimal"
              value={draft.net}
              onFocus={(e) => e.target.select()}
              onChange={(e) => commitNet(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') setEditing(false);
              }}
            />
          </label>
          <div className="ml-auto flex items-center gap-1.5 pb-0.5">
            {hasDiscount && (
              <button
                type="button"
                onClick={() => onChange({ amount: full, discounted_amount: null })}
                className="text-[12px] font-medium text-[#2383E2] hover:underline px-1 py-1"
              >
                azzera sconto
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-[12px] font-medium px-3 py-1.5 rounded-md bg-[#37352F] text-white hover:opacity-90 transition-opacity"
            >
              Fatto
            </button>
          </div>
        </div>
        <p className="mt-1.5 text-[11px] text-black/45 dark:text-white/45">
          Percentuale e prezzo finale si inseguono: scrivi in uno e l'altro si aggiorna.
        </p>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="w-full flex items-baseline gap-2.5 flex-wrap px-2 py-1 -mx-2 rounded text-left transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.04] group cursor-pointer"
    >
      <span className="text-[26px] font-semibold tracking-tight tabular-nums text-black dark:text-white">
        {fmt(net)}
        {periodSuffix && (
          <span className="text-[14px] font-medium text-black/50 dark:text-white/50 tracking-normal">
            {periodSuffix}
          </span>
        )}
      </span>
      {hasDiscount && (
        <>
          <span className="text-[13px] line-through tabular-nums text-black/35 dark:text-white/35">{fmt(full)}</span>
          <span className="text-[11px] font-medium px-1.5 py-0.5 rounded text-[#0F7B6C] bg-[#0F7B6C]/10">
            −{Math.round(pct)}%
          </span>
        </>
      )}
      {!hasDiscount && zeroHint && (
        <span className="text-[13px] text-black/35 dark:text-white/35">{zeroHint}</span>
      )}
      <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-black/50 dark:text-white/50 opacity-0 group-hover:opacity-100 transition-opacity">
        <Pencil size={11} />
        modifica prezzo
      </span>
    </button>
  );
}
